// @flow

import * as React from 'react';
import ReactAI from 'react-appinsights';
import DropDownInput from "./DropDownInput";
import TubesInput from "./TubesInput";
import OptionManager from "../common/OptionManager";
import DependencyManager from "../common/DependencyManager";

import type { NumTubesCallback } from "./TubesInput"
import type {ComponentMeta, UnmountHandler} from "../common/CommonTypes";
import CalculationSectionHeader from "./CalculationSectionHeader";
import CalculationSectionSubHeader from "./CalculationSectionSubHeader";
import NumberInput from "./NumberInput";

type Props = {
    optionManager: OptionManager,
    dependencyManager: DependencyManager,

    onChange: (event: SyntheticEvent<>) => void,
    onBlur: (event: SyntheticEvent<>) => void,
    onUnmount: UnmountHandler,
    addTube: NumTubesCallback,
    removeTube: NumTubesCallback,

    typeOfLandscapeBefore: number,
    coatingBefore: number,
    streetTypeBefore: number,

    typeOfLandscapeBeforeMeta: ComponentMeta,
    coatingBeforeMeta: ComponentMeta,
    streetTypeBeforeMeta: ComponentMeta,
    
    numTubes: number,
    numTubesName: string,
    tubeValuesNamePrefix: string,
    tubeValues: {[valueName: string]: number},
    tubeMetas: {[valueName: string]: ComponentMeta},

    techniquesToCompare: number,

    phaseNr: number,

};

class CalculationBasicInfoInput extends React.Component<Props> {
    
    render() {

        const optionManager = this.props.optionManager;
        const dependencyManager = this.props.dependencyManager;

        const onChange = this.props.onChange;
        const onBlur = this.props.onBlur;
        const onUnmount = this.props.onUnmount;
        const addTube = this.props.addTube;
        const removeTube = this.props.removeTube;
        
        const typeOfLandscapeBefore = this.props.typeOfLandscapeBefore;
        const coatingBefore = this.props.coatingBefore;
        const streetTypeBefore = this.props.streetTypeBefore;
        
        const numTubes = this.props.numTubes;
        const numTubesName = this.props.numTubesName;
        const tubeValuesNamePrefix = this.props.tubeValuesNamePrefix;
        const tubeValues = this.props.tubeValues;
        const tubeMetas = this.props.tubeMetas;
        
        const minTubes = this.props.techniquesToCompare === optionManager.ids.TECH_DD ? 0 : 1;     
        
        const htmlHelp = `<img src="/images/shaft-tubes.png"/><br/><p>Ange endast de befintliga rör som skall förnyas.</p><p>I en och samma etapp kan endast en rörlängd väljas för alla rör.<br/>Vid olika rörlängd dela upp och beräkna i flera etapper.</p><p>Utgångspunkten är AMA där den vanligaste kombinationen i en rörgrav är: avlopp (underst) vatten (mitten) och dagvatten (överst).</p><p>Max 3 rör kan väljas och måste ligga på olika djup. Det är möjligt att välja flera rör av samma rörtyp dock med placering på olika nivå.</p>`;
        const htmlHelpNoTubes = `<img src="/images/shaft-tubes.png"/><br/><p>Du har valt att det inte finns några befintliga rör i etappen utan att helt nya rör ska konstrueras med styrd borrning utan att ersätta existerande rör. Ange längd för kommande rör här. </p><p>I en och samma etapp kan endast en rörlängd väljas för alla rör.<br/>Vid olika rörlängd dela upp och beräkna i flera etapper.</p><p>Utgångspunkten är AMA där den vanligaste kombinationen i en rörgrav är: avlopp (underst) vatten (mitten) och dagvatten (överst).</p><p>Max 3 rör kan väljas och måste ligga på olika djup. Det är möjligt att välja flera rör av samma rörtyp dock med placering på olika nivå.</p>`;
        
        let tubeTypes = optionManager.availableSelections.tubeType;
        let maxTubes = 3;
        if(this.props.techniquesToCompare === optionManager.ids.TECH_PUL) {
            tubeTypes = optionManager.availableSelections.tubeTypePul;
            maxTubes = tubeTypes.length;
        }
        
        return (
            <div className="calculation-basic-info input-section">

                <CalculationSectionHeader text="Platsen före" id={ `place-before-${this.props.phaseNr}` } /> 

                <div className="input-section-content">
                    <div className="row">
                        <DropDownInput htmlName="typeOfLandscapeBefore"
                                       onChange={onChange}
                                       onBlur={onBlur}
                                       onUnmount={onUnmount}
                                       labelText="Typ av landskap"
                                       phaseNr={this.props.phaseNr}
                                       extraClassFormGroup="col-sm-6"
                                       value={typeOfLandscapeBefore}
                                       meta={this.props.typeOfLandscapeBeforeMeta}
                                       includeEmptyOption={false}
                                       values={optionManager.availableSelections.typeOfLandscape} />
                    
                    
                        <DropDownInput  htmlName="coatingBefore"
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        onUnmount={onUnmount}
                                        labelText="Beläggning"
                                        phaseNr={this.props.phaseNr}
                                        extraClassFormGroup="col-sm-6"
                                        value={coatingBefore}
                                        meta={this.props.coatingBeforeMeta}
                                        includeEmptyOption={false}
                                        values={dependencyManager.getCoatings(typeOfLandscapeBefore)} />
                    </div>
    
                    <div className="row">
                        { coatingBefore === optionManager.ids.COATING_ASPHALT &&
                        <DropDownInput htmlName="streetTypeBefore"
                                       onChange={onChange}
                                       onBlur={onBlur}
                                       onUnmount={onUnmount}
                                       labelText="Gatutyp"
                                       phaseNr={this.props.phaseNr}
                                       extraClassFormGroup="col-sm-6"
                                       value={streetTypeBefore}
                                       includeEmptyOption={false}
                                       meta={this.props.streetTypeBeforeMeta}
                                       values={optionManager.availableSelections.streetType} /> }
                                       
                    </div>
    
                    {numTubes > 0 &&  <div>
                        <CalculationSectionSubHeader text="Befintliga rör" helpHtml={htmlHelp}/>
                        <TubesInput numTubes={numTubes}
                        namePrefix={tubeValuesNamePrefix}
                        minTubes={minTubes}
                        maxTubes={maxTubes}
                        values={tubeValues}
                        metas={tubeMetas}
                        onChange={onChange}
                        onBlur={onBlur}
                        onUnmount={onUnmount}
                        numTubesName={numTubesName}
                        addTube={addTube}
                        removeTube={removeTube}
                        isNoDig={false}
                        phaseNr={this.props.phaseNr}
                        tubeTypes={tubeTypes}
                        tubeMaterialCallback={dependencyManager.getTubeMaterials.bind(dependencyManager)}
                        tubeDiameterCallback={dependencyManager.getTubeDiameters.bind(dependencyManager)} />
                    </div>
                    }
    
                    {numTubes === 0 &&  <div>
                        <CalculationSectionSubHeader text="Nya rör (inga befintliga rör)" helpHtml={htmlHelpNoTubes}/>
                        <NumberInput    htmlName="existingTube1TubeLength"
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        onUnmount={onUnmount}
                                        labelText="Nya rörens längd (m)"
                                        phaseNr={this.props.phaseNr}
                                        value={tubeValues["existingTube1TubeLength"]}
                                        meta={tubeMetas["existingTube1TubeLength"]}
                                        min={0}
                                        disabled={false}
                        />
                        <button type="button" className="btn btn-info btn-sm print-hide" onClick={() => addTube(numTubesName)}>Lägg till befintligt rör</button>
                    </div>
                    }
                </div>
            </div>
        );

    }

}

export default ReactAI.withTracking(CalculationBasicInfoInput);



// WEBPACK FOOTER //
// ./src/components/CalculationBasicInfoInput.js